import { Component, inject } from '@angular/core';
// import { AdvisoryToolService } from '@app/advisory-tool/services/advisory-tool.service';
import { FeaturesService } from '@app/core/services/features.service';
import { NotesService } from '@app/core/services/notes.service';
import { AuthSelectors } from '@app/core/state/auth/auth.selectors';
import { initialFeaturesState } from '@app/core/state/feature/feature.reducer';
import { FeatureSelectors } from '@app/core/state/feature/feature.selectors';
import { FeatureType, PrivilegeType } from '@app/core/state/types';
import { IconProps, NavMenuHeader, NavMenuSection, NavMenuSectionItem, OrganizationMenu } from '@bl/components';
import { AnalyticService, AnalyticTracking } from '@core/entity/analytics/analytic-tracking';
import { CollaborationPackageSelectors } from '@core/state/collaboration-packages/collaboration-packages.selectors';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, combineLatest, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { AnalyticsAction } from '../core/entity/analytics/analytics-actions';
import { AnalyticsCategory } from '../core/entity/analytics/analytics-categories';
import { CountryType, isNetherlands } from '../core/entity/locale';
import { getTranslationProvider } from '../core/i18n/getTranslationProvider';
import { TranslatableComponent } from '../core/i18n/TranslatableComponent';
import { AllInclusiveService } from '../core/services/all-inclusive.service';
import { AuthActions } from '../core/state/auth/auth.actions';
import { CollaborationPackageActions } from '../core/state/collaboration-packages/collaboration-packages.actions';
import { FirmSelectors } from '../core/state/firm/firm.selectors';
import { ToastActions } from '../core/state/toast/toast.actions';
import { FirmSettingsDialog } from '../firm-settings/firm.settings.dialog';
import { openTranslatableDialog } from '../shared/misc/openDialog';
import { showTodoDialog } from '../shared/misc/showTodoDialog';

@Component({
  templateUrl: './main-app.component.html',
  host: { class: 'flex flex-col h-full' },
  providers: [getTranslationProvider('MainAppComponent')],
})
export class MainAppComponent extends TranslatableComponent {
  public menuSections$: Observable<NavMenuSection[]>;
  public selectCompanyMenu$: Observable<OrganizationMenu>;
  public menuHeader$: Observable<NavMenuHeader>;
  public mobileMenuState: 'open' | 'closed' = 'closed';

  private hasAdminPrivilege: boolean;
  private features: FeatureType;
  private analyticService: AnalyticTracking;

  // eslint-disable-next-line max-params
  constructor(
    featuresService: FeaturesService,
    private notesService: NotesService,
    // private advisoryService: AdvisoryToolService,
    private allInclusiveService: AllInclusiveService,
    private dialogService: DialogService,
  ) {
    super();
    this.analyticService = inject(AnalyticService);
    featuresService.init();

    this.hasAdminPrivilege = false;
    this.features = { ...initialFeaturesState.features };

    this.store
      .select(AuthSelectors.selectAuthUser)
      .pipe(filter((user) => Boolean(user)))
      .subscribe((user) => {
        this.hasAdminPrivilege = user.privilege === PrivilegeType.ADMIN;
      });

    this.store
      .select(FeatureSelectors.selectFeaturesState)
      .pipe(filter((features) => Boolean(features?.initialized)))
      .subscribe(({ features }) => {
        this.features = features;
      });

    this.setupNavMenuObservables();
  }

  isAdminAndFeatureIsActive = (feature: string): boolean => this.features[feature] && this.hasAdminPrivilege;

  toggleMenuIfMobile() {
    this.mobileMenuState = this.mobileMenuState === 'open' ? 'closed' : 'open';
  }

  onMenuToggle(isExpanded: boolean) {
    this.analyticService.trackEvent({
      category: AnalyticsCategory.MainNavigationMenu,
      action: AnalyticsAction.MainNavigationMenuToggled,
      name: isExpanded ? 'Expanded' : 'Collapsed',
    });
  }

  logout() {
    this.store.dispatch(ToastActions.clearsAllToastMessages({}));
    this.store.dispatch(AuthActions.resetAuthStateAndStorage());
    this.store.dispatch(CollaborationPackageActions.clearState({}));
    this.router.navigate(['login']);
  }

  showNewNoteDialog() {
    const deps = {
      notesService: this.notesService,
      dialogService: this.dialogService,
      translationService: this.translationService,
      store: this.store,
    };
    showTodoDialog({ deps, id: -1 });
  }

  showSettingsDialog() {
    const deps = {
      dialogService: this.dialogService,
      translationService: this.translationService,
      store: this.store,
    };
    openTranslatableDialog({ deps, componentType: FirmSettingsDialog, data: {} });
  }

  private setupNavMenuObservables() {
    this.menuHeader$ = of({
      lundifyProductName: 'Workflow',
    });

    this.menuSections$ = this.setupMenuSections();

    this.selectCompanyMenu$ = this.store.select(FirmSelectors.selectFirm).pipe(
      filter((firm) => Boolean(firm)),
      map((firm) => ({ id: firm.id, text: firm.name })),
      map((item) => ({
        items: [item],
        value: item,
        readonly: true,
        // required properties for OrganizationMenu, but not relevant for workflow (remove when fixed in NavMenu component)
        placeholder: '',
        returnButtonText: '',
        searchFieldPlaceholder: '',
      })),
    );
  }

  private getUnfinishedTodosCountForMenu(): Observable<number> {
    return this.notesService.unfinishedTodos$.pipe(
      startWith(null),
      catchError(() => of(null)),
    );
  }

  // private getAdvisoryNotificationCountForMenu(): Observable<number> {
  //   return this.advisoryService.showNotificationCount$.pipe(
  //     switchMap((showAdvisoryNotifications) =>
  //       showAdvisoryNotifications ? this.advisoryService.notificationsCount$ : of(null),
  //     ),
  //     startWith(null),
  //     catchError(() => of(null)),
  //   );
  // }

  private getCollaborationPackageCountForMenu(): Observable<number> {
    return this.store.select(CollaborationPackageSelectors.selectNumberOfMissingCollaborationPackages).pipe(
      startWith(null),
      catchError(() => of(null)),
    );
  }

  private setupMenuSections(): Observable<NavMenuSection[]> {
    const menuTodos$ = combineLatest([
      this.getUnfinishedTodosCountForMenu(),
      this.getCollaborationPackageCountForMenu(),
      // this.getAdvisoryNotificationCountForMenu(),
    ]).pipe(
      map(([unfinishedTodos, numberOfMissingCollaborationPackages]) => ({
        unfinishedTodos,
        numberOfMissingCollaborationPackages,
        // advisoryNotification,
      })),
    );

    return this.store.select(FirmSelectors.selectCountryCode).pipe(
      filter((country): country is CountryType => Boolean(country)),
      switchMap((country) => menuTodos$.pipe(map((todos) => ({ country, ...todos })))),
      map((menuTodoData) => this.getMenuSectionsForCountry(menuTodoData)),
      switchMap((sections) =>
        this.onTranslationChangeMapTo((translatedSections) => {
          return sections
            .filter((section) => !section.exclude)
            .map((section) => this.toMenuSectionItem(section, translatedSections));
        }),
      ),
      map((items) => [{ items }]),
    );
  }

  private toMenuSectionItem(
    section: {
      key: string;
      icon: IconProps['name'];
      route: string;
      active?: boolean;
      todos?: number;
    },
    translations: Record<string, string>,
  ): NavMenuSectionItem {
    return {
      icon: section.icon,
      title: translations[section.key],
      callback: () => this.routeTo(section.route),
      active: Boolean(section.active),
      todos: section.todos,
    };
  }

  private routeTo(path: string): void {
    this.router.navigate([path]);
  }

  // eslint-disable-next-line max-lines-per-function
  private getMenuSectionsForCountry({
    country,
    unfinishedTodos,
    numberOfMissingCollaborationPackages,
    // advisoryNotification,
  }: {
    country: CountryType;
    unfinishedTodos: number;
    numberOfMissingCollaborationPackages: number;
    // advisoryNotification: number;
  }): {
    key: string;
    icon: IconProps['name'];
    route: string;
    active?: boolean;
    todos?: number;
    exclude?: boolean;
  }[] {
    if (isNetherlands(country)) {
      return [
        { key: 'start', icon: 'house-chimney', route: '/start', active: true },
        { key: 'todos', icon: 'list-check', route: '/todo', todos: unfinishedTodos },
        { key: 'notifications', icon: 'mailbox-flag-up', route: '/notifications' },
        { key: 'clients', icon: 'address-card', route: '/clients' },
        {
          key: 'collaborationPackages',
          icon: 'people-line',
          route: '/collaborationpackages',
          todos: numberOfMissingCollaborationPackages,
          exclude: !this.allInclusiveService.isLoggedInWithBLTAService,
        },
      ];
    }

    return [
      { key: 'start', icon: 'house-chimney', route: '/start', active: true },
      { key: 'allActivities', icon: 'circle-quarter-stroke', route: '/activities' },
      { key: 'accounting', icon: 'book-open', route: '/accounting' },
      { key: 'salaries', icon: 'hand-holding-dollar', route: '/salaries' },
      { key: 'taxDeclaration', icon: 'inbox', route: '/taxdeclaration' },
      { key: 'annualReport', icon: 'calendar-check', route: '/closing' },
      { key: 'incomeTaxReturn', icon: 'inbox-full', route: '/incometaxdeclaration' },
      { key: 'controlReports', icon: 'square-info', route: '/incomestatement' },
      { key: 'todos', icon: 'list-check', route: '/todo', todos: unfinishedTodos },
      { key: 'notifications', icon: 'mailbox-flag-up', route: '/notifications' },
      { key: 'clients', icon: 'address-card', route: '/clients' },
      {
        key: 'collaborationPackages',
        icon: 'people-line',
        route: '/collaborationpackages',
        todos: numberOfMissingCollaborationPackages,
        exclude: !this.allInclusiveService.isLoggedInWithBLTAService,
      },
      // TODO:
      // Städa bort allt med "advisory tools" så snart som möjligt efter årsskiftet 26/27

      // {
      //   key: 'advisoryTools',
      //   icon: 'lightbulb-exclamation',
      //   route: '/advisorytool',
      //   todos: advisoryNotification,
      // },
      { key: 'sustainabilityReporting', icon: 'file-certificate', route: '/sustainability' },
    ];
  }
}
